import { useFeatureAccess } from "./access-control/hooks/useFeatureAccess";
import { ACCESS_GROUPS } from "./access-control/models/access-groups.models";
import { FEATURE_KEYS } from "./access-control/models/role-feature.models";
import AdminFlowsPage from "./admin-agent/pages/AdminFlowsPage";
import AdminDefaultRedirect from "./admin/components/admin-page/AdminDefaultRedirect";
import { ADMIN_PATHS } from "./admin/models/admin.models";
import AdminAiAcScanPage from "./admin/pages/AdminAiAcScanPage";
import AdminAiFeaturesScanPage from "./admin/pages/AdminAiFeaturesScanPage";
import AdminAiNullsScanPage from "./admin/pages/AdminAiNullsScanPage";
import AdminAiResilienceScanPage from "./admin/pages/AdminAiResilienceScanPage";
import AdminAiScansPage from "./admin/pages/AdminAiScansPage";
import AdminAiSecurityScanPage from "./admin/pages/AdminAiSecurityScanPage";
import AdminAiValidationsScanPage from "./admin/pages/AdminAiValidationsScanPage";
import AdminBqTqPage from "./admin/pages/AdminBqTqPage";
import AdminCertificatesSettingsPage from "./admin/pages/AdminCertificatesSettingsPage";
import AdminCrewReportPage from "./admin/pages/AdminCrewReportPage";
import AdminDashboardPage from "./admin/pages/AdminDashboardPage";
import AdminDebugPage from "./admin/pages/AdminDebugPage";
import AdminEmailPage from "./admin/pages/AdminEmailPage";
import AdminKeysSecretsPage from "./admin/pages/AdminKeysSecretsPage";
import AdminKpisPage from "./admin/pages/AdminKpisPage";
import AdminMonitoringPage from "./admin/pages/AdminMonitoringPage";
import AdminOnboardingPage from "./admin/pages/AdminOnboardingPage";
import AdminPage from "./admin/pages/AdminPage";
import AdminPaintSchemesPage from "./admin/pages/AdminPaintSchemesPage";
import AdminPnlDebugPage from "./admin/pages/AdminPnlDebugPage";
import AdminRoleFeatureMatrixPage from "./admin/pages/AdminRoleFeatureMatrixPage";
import AdminSeniorVoUsersPage from "./admin/pages/AdminSeniorVoUsersPage";
import AdminTechFormInboxDebugPage from "./admin/pages/AdminTechFormInboxDebugPage";
import AdminUwhcPage from "./admin/pages/AdminUwhcPage";
import { ADMIN_ENTRY_ACCESS_RULES } from "./admin/utils/admin-entry-access.constants";
import { ThemeEffect } from "./appearance/ThemeEffect";
import { AccessControl } from "./auth/components/AccessControl";
import { RedirectToLogin } from "./auth/components/RedirectToLogin";
import { Callback } from "./auth/pages/Callback";
import LoginPage from "./auth/pages/LoginPage";
import { Logout } from "./auth/pages/Logout";
import { oktaAuth } from "./auth/services/oktaAuth";
import { BalticQuestionnaire } from "./baltic-form/pages/BalticQuestionnaire";
import Certificates from "./certificates/pages/Certificates";
import DocumentPage from "./documents/pages/DocumentPage";
import MissingDocumentsPage from "./documents/pages/MissingDocuments";
import RequestDocumentsPage from "./documents/pages/RequestDocumentsPage";
import ExternalLandingPage from "./external/pages/ExternalLandingPage";
import HoldCleaningVoyagePage from "./hold-cleaning-intelligence/pages/HoldCleaningVoyagePage";
import HoldCleaningVoyagesPage from "./hold-cleaning-intelligence/pages/HoldCleaningVoyagesPage";
import { ChatOverlay } from "./jarvis/components/ChatOverlay";
import { OtHullCleaningChat } from "./jarvis/components/OtHullCleaningChat";
import Home from "./my-vessels/pages/Home";
import CalculatorsPage from "./pbi/calculators/pages/CalculatorsPage";
import HullCleaningFollower from "./pbi/hull-cleaning/pages/HullCleaningFollower";
import HullPaintsInfoActiveVessels from "./pbi/hull-paints/pages/HullPaintsInfoActiveVessels";
import SisterSearcher from "./pbi/sister-searcher/pages/SisterSearcher";
import TfHullEvaluationsPage from "./pbi/tf-hull-evaluations/pages/TfHullEvaluationsPage";
import VesselCardRouteGate from "./pbi/vessel-card/components/VesselCardRouteGate";
import VesselDataPage from "./pbi/vessel-data/pages/VesselDataPage";
import VesselEvaluationFollower from "./pbi/vessel-evaluation/pages/VesselEvaluationFollower";
import VesselSearcherPage from "./pbi/vessel-searcher/pages/VesselSearcherPage";
import VoyageEvaluationPage from "./pbi/voyage-evaluation/pages/VoyageEvaluationPage";
import SearchVessels from "./search-vessels/pages/SearchVessels";
import { featureFlagsAtom } from "./shared/atoms";
import LoadingWrapper from "./shared/components/LoadingWrapper";
import ErrorPage from "./shared/pages/ErrorPage";
import TechnicalQuestionnaire from "./tech-form/pages/TechnicalQuestionnaire";
import VesselDetailsPage from "./vessel-details/pages/VesselDetails";
import VoyageAssessmentPage from "./voyage-assessment/pages/VoyageAssessmentPage";
import VoyagePnlPage from "./voyage-pnl/pages/VoyagePnl";
import VoyagePnlList from "./voyage-pnl/pages/VoyagePnlList";
import VoyagePage from "./voyage-sequence/pages/Voyage";
import VoyageDetailsPage from "./voyage-sequence/pages/VoyageDetails";
import OktaAuth, { toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { ClickToComponent } from "click-to-react-component";
import { useAtomValue } from "jotai";
import { lazy, Suspense } from "react";
import { matchPath, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "react-tooltip/dist/react-tooltip.css";

const VesselClusteringPage = lazy(() => import("./pbi/vessel-clustering/pages/VesselClusteringPage"));

const ADMIN_MONITORING_ENTRY_ACCESS_RULES = [
  { featureKey: FEATURE_KEYS.ADMIN_DEBUG_MONITORING },
  { featureKey: FEATURE_KEYS.ADMIN_REF_PARAMETERS_UWHC_VENDORS },
  { featureKey: FEATURE_KEYS.HOLD_CLEANING_ADMIN },
  {
    featureKey: FEATURE_KEYS.TQ_ADMIN,
    groups: { anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] },
  },
] as const;
const ADMIN_TQ_BQ_MONITORING_ACCESS_RULES = [
  {
    featureKey: FEATURE_KEYS.TQ_ADMIN,
    groups: { anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] },
  },
] as const;
const ADMIN_CRON_ACCESS_RULES = [
  { featureKey: FEATURE_KEYS.ADMIN_DEBUG_MONITORING },
  { featureKey: FEATURE_KEYS.HOLD_CLEANING_ADMIN },
] as const;

// App routing migrated according to Inline Login POC (we used template routing instead).
// https://okta-auth-poc.dev.cglcloud.in/ - Apr '24 Adrian
async function restoreOriginalUri(_oktaAuth: OktaAuth, originalUri: string) {
  const url = toRelativeUrl(originalUri || "/", window.location.origin);
  window.location.href = url;
}

const App: React.FC = () => {
  // const auth = useAuthContext();
  // const { sub } = auth.state.user?.accessToken || {};
  const featureFlags = useAtomValue(featureFlagsAtom);
  const location = useLocation();
  const isVoyagePage = !!matchPath("/voyage/:tcCode/:voyageNo?", location.pathname);
  const isAdminPage = !!matchPath("/admin/*", location.pathname);
  const { hasAccess: canUseUwhcAssistant } = useFeatureAccess(FEATURE_KEYS.JARVIS_UWHC_AI_AGENT);

  return (
    <>
      <ThemeEffect useTailwindDark={true} />

      <ClickToComponent editor={process.env.REACT_APP_CLICK_TO_COMPONENT_EDITOR ?? "vscode"} />

      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        {featureFlags.chat_enabled && !isVoyagePage && !isAdminPage && canUseUwhcAssistant && (
          <ChatOverlay>
            <OtHullCleaningChat />
          </ChatOverlay>
        )}
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/logout" element={<Logout />} />
          <Route path="/auth/callback" element={<Callback />} />

          {/* Secured */}
          <Route path="/" element={<RedirectToLogin />}>
            <Route index element={<Navigate to="home" />} />

            {/* Admin */}
            <Route element={<AccessControl accessRules={ADMIN_ENTRY_ACCESS_RULES} />}>
              <Route path={ADMIN_PATHS.root} element={<AdminPage />}>
                <Route index element={<AdminDefaultRedirect />} />
                <Route element={<AccessControl accessRules={ADMIN_CRON_ACCESS_RULES} />}>
                  <Route path="debug" element={<Navigate to={ADMIN_PATHS.debugCronJobs} replace />} />
                  <Route path="debug/cron-jobs" element={<AdminDebugPage section="cronJobs" />} />
                  <Route path="monitoring/cron-jobs" element={<AdminMonitoringPage section="cronJobs" />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_DEBUG_MONITORING} />}>
                  <Route path="debug/tech-form" element={<AdminDebugPage section="techForm" />} />
                  <Route path="debug/tech-form-inbox" element={<AdminTechFormInboxDebugPage />} />
                  <Route path="debug/baltic-questionnaire" element={<AdminDebugPage section="balticQuestionnaire" />} />
                  <Route path="monitoring/documents" element={<AdminMonitoringPage section="documents" />} />
                  <Route path="monitoring/vessels" element={<AdminMonitoringPage section="vessels" />} />
                  <Route path="monitoring/voyages" element={<AdminMonitoringPage section="voyages" />} />
                  <Route path="monitoring/voyage-pnl" element={<AdminMonitoringPage section="voyagePnl" />} />
                  <Route path="monitoring/notifications" element={<AdminMonitoringPage section="notifications" />} />
                </Route>
                <Route element={<AccessControl accessRules={ADMIN_TQ_BQ_MONITORING_ACCESS_RULES} />}>
                  <Route path="monitoring/tech-forms" element={<AdminMonitoringPage section="techForms" />} />
                  <Route
                    path="monitoring/baltic-questionnaires"
                    element={<AdminMonitoringPage section="balticQuestionnaires" />}
                  />
                </Route>
                <Route element={<AccessControl accessRules={ADMIN_MONITORING_ENTRY_ACCESS_RULES} />}>
                  <Route path="monitoring" element={<AdminMonitoringPage />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_REF_PARAMETERS_UWHC_VENDORS} />}>
                  <Route path="monitoring/uwhc" element={<AdminMonitoringPage section="uwhc" />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_REF_PARAMETERS_UWHC_VENDORS} />}>
                  <Route path="uwhc" element={<AdminUwhcPage />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_REF_PARAMETERS_PAINT_SCHEMES} />}>
                  <Route path="paint-schemes" element={<AdminPaintSchemesPage />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_REF_PARAMETERS_SENIOR_VO} />}>
                  <Route path="voyage-pnl" element={<AdminSeniorVoUsersPage />} />
                  <Route path="uwhc/senior-vo-users" element={<Navigate to={ADMIN_PATHS.voyagePnl} replace />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_REF_PARAMETERS_CERTS} />}>
                  <Route path="certificates-settings" element={<AdminCertificatesSettingsPage />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_ANALYTICS_CERTS} />}>
                  <Route path="kpis" element={<AdminKpisPage />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_CREW_REPORT} />}>
                  <Route path="crew-report" element={<AdminCrewReportPage />} />
                </Route>
                <Route element={<AccessControl group={ACCESS_GROUPS.Admin} />}>
                  <Route path="ai-flows/:flowView?" element={<AdminFlowsPage />} />
                  <Route path="ai-scans" element={<AdminAiScansPage />}>
                    <Route index element={<Navigate to="features" replace />} />
                    <Route path="features" element={<AdminAiFeaturesScanPage />} />
                    <Route path="ac" element={<AdminAiAcScanPage />} />
                    <Route path="security" element={<AdminAiSecurityScanPage />} />
                    <Route path="nulls" element={<AdminAiNullsScanPage />} />
                    <Route path="validations" element={<AdminAiValidationsScanPage />} />
                    <Route path="resilience" element={<AdminAiResilienceScanPage />} />
                    <Route path="*" element={<Navigate to="/admin/ai-scans/features" replace />} />
                  </Route>
                  <Route path="dashboard" element={<AdminDashboardPage />} />
                  <Route
                    element={
                      <AccessControl requiredGroups={{ allOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.InternalUsers] }} />
                    }
                  >
                    <Route path="emails" element={<AdminEmailPage />} />
                  </Route>
                  <Route path="debug/onboarding" element={<AdminOnboardingPage />} />
                  <Route path="debug/voyage-pnl" element={<AdminPnlDebugPage />} />
                  <Route path="keys-secrets" element={<AdminKeysSecretsPage />} />
                </Route>
                <Route element={<AccessControl featureKey={FEATURE_KEYS.ADMIN_ACCESS_CONTROL_ROLE_FEATURES} />}>
                  <Route path="access-control/role-features" element={<AdminRoleFeatureMatrixPage />} />
                </Route>
                <Route
                  element={
                    <AccessControl
                      featureKey={FEATURE_KEYS.TQ_ADMIN}
                      requiredGroups={{ anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] }}
                    />
                  }
                >
                  <Route path="bq-tq-admin" element={<AdminBqTqPage />} />
                </Route>
              </Route>
            </Route>
            {featureFlags.hold_cleaning_intelligence_enabled && (
              <Route
                element={
                  <AccessControl
                    featureKey={FEATURE_KEYS.HOLD_CLEANING_ACCESS}
                    requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers] }}
                  />
                }
              >
                <Route path="/hold-cleaning" element={<HoldCleaningVoyagesPage />} />
                <Route path="/hold-cleaning/voyages/:voyageId" element={<HoldCleaningVoyagePage />} />
                <Route path="/hold-cleaning/vessels/:imo" element={<HoldCleaningVoyagePage />} />
              </Route>
            )}
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.TQ_ADMIN}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] }}
                />
              }
            >
              <Route
                path={`${ADMIN_PATHS.bqTqAdminTechnicalForm}/:imoNumber/:vesselName/:pageName`}
                element={
                  <TechnicalQuestionnaire
                    isCompletedForm={false}
                    routeBasePath={ADMIN_PATHS.bqTqAdminTechnicalForm}
                    landingPagePath={ADMIN_PATHS.bqTqAdmin}
                  />
                }
              />
              <Route
                path={`${ADMIN_PATHS.bqTqAdminBalticQuestionnaire}/:imoNumber/:vesselName/:pageName`}
                element={
                  <BalticQuestionnaire
                    isCompletedForm={false}
                    routeBasePath={ADMIN_PATHS.bqTqAdminBalticQuestionnaire}
                    landingPagePath={ADMIN_PATHS.bqTqAdmin}
                  />
                }
              />
            </Route>

            <Route path="/calculators" element={<CalculatorsPage />} />
            <Route
              element={
                <AccessControl requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers, ACCESS_GROUPS.ExternalUsers] }} />
              }
            >
              <Route path="/vessel-card" element={<VesselCardRouteGate />} />
              <Route path="/vessel-card/:imo" element={<VesselCardRouteGate />} />
            </Route>
            <Route path="/vessel-data" element={<VesselDataPage />} />
            <Route path="/hull-paints-info-active-vessels" element={<HullPaintsInfoActiveVessels />} />
            <Route path="/hull-cleaning-follower" element={<HullCleaningFollower />} />
            <Route path="/tech-forms-hull-evaluation" element={<TfHullEvaluationsPage />} />
            <Route path="/tf-hull-evaluations" element={<TfHullEvaluationsPage />} />
            <Route path="/vessel-evaluation-follower" element={<VesselEvaluationFollower />} />
            <Route path="/voyage-evaluation-follower" element={<VoyageEvaluationPage />} />
            <Route path="/vessel-searcher" element={<VesselSearcherPage />} />
            <Route path="/sister-vessel-search" element={<SisterSearcher />} />
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.SISTERSHIP_ACCESS}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers, ACCESS_GROUPS.ExternalUsers] }}
                />
              }
            >
              <Route
                path="/vessel-clustering"
                element={
                  <Suspense fallback={<LoadingWrapper isLoading={true} loadingIndicatorSize="lg" />}>
                    <VesselClusteringPage />
                  </Suspense>
                }
              />
            </Route>

            {/* Internal */}
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.CORE_MY_VESSELS}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers] }}
                />
              }
            >
              <Route path="/home" element={<Home />} />
            </Route>
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.CORE_SEARCH_VESSELS}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers] }}
                />
              }
            >
              <Route path="/vessels" element={<SearchVessels />} />
              <Route path="/voyage/:tcCode/:voyageNo?" element={<VoyagePage />} />
              <Route path="/voyage/details/:tcCode/:voyageNo" element={<VoyageDetailsPage />} />
              <Route path="/vessel/details/:imoNumber/:vesselCode/:vesselName" element={<VesselDetailsPage />} />
              <Route
                path="/vessel/details/:imoNumber/:vesselCode/:vesselName/:requestId"
                element={<VesselDetailsPage />}
              />
              {featureFlags.owner_rating_enabled && (
                <Route path="/voyage/assessment/:tcCode/:voyageNo" element={<VoyageAssessmentPage />} />
              )}
            </Route>
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.CORE_CERTS}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers] }}
                />
              }
            >
              <Route path="/certificates" element={<Certificates />} />
              <Route path="/documents" element={<DocumentPage />} />
              <Route path="/documents/request" element={<RequestDocumentsPage />} />
            </Route>
            <Route
              element={
                <AccessControl
                  featureKeys={[FEATURE_KEYS.VOYAGE_PNL, FEATURE_KEYS.REVIEW_PNL]}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers] }}
                />
              }
            >
              <Route path="/voyage/details/:tcCode/:voyageNo/pnl" element={<VoyagePnlPage />} />
            </Route>
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.REVIEW_PNL}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.InternalUsers] }}
                />
              }
            >
              <Route path="/voyage/details/:tcCode/:voyageNo/pnl/:isApprover" element={<VoyagePnlPage />} />
              <Route path="/voyage/pnlList" element={<VoyagePnlList />} />
            </Route>

            {/* External */}
            <Route element={<AccessControl group={[ACCESS_GROUPS.ExternalUsers, ACCESS_GROUPS.VesselPerformance]} />}>
              <Route path="/landingPage" element={<ExternalLandingPage />} />
            </Route>
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.CORE_CERTS}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.ExternalUsers] }}
                />
              }
            >
              <Route path="/missing-documents" element={<MissingDocumentsPage />} />
            </Route>
            <Route
              element={
                <AccessControl
                  featureKey={FEATURE_KEYS.TQ_FORMS}
                  requiredGroups={{ anyOf: [ACCESS_GROUPS.ExternalUsers] }}
                />
              }
            >
              <Route
                path="/technicalForm/:imoNumber/:vesselName/:pageName"
                element={<TechnicalQuestionnaire isCompletedForm={false} />}
              />
              <Route
                path="/technicalForm/completed/:imoNumber/:vesselName/:pageName"
                element={<TechnicalQuestionnaire isCompletedForm={true} />}
              />
              <Route
                path="/balticForm/:imoNumber/:vesselName/:pageName"
                element={<BalticQuestionnaire isCompletedForm={false} />}
              />
              <Route
                path="/balticForm/completed/:imoNumber/:vesselName/:pageName"
                element={<BalticQuestionnaire isCompletedForm={true} />}
              />
            </Route>
          </Route>

          {/* Error handling */}
          <Route path="*" element={<Navigate to="errors/404" />} />
          <Route path="/errors/:errorCode" element={<ErrorPage />} />
        </Routes>
      </Security>
    </>
  );
};

export default App;
